import { Application } from 'express';
import { Server } from 'http';
import { Logger, createLogger } from '@unidev-hub/logger';
import { ConfigManager, createConfigManager } from '@unidev-hub/config';
import { DatabaseConnection } from '@unidev-hub/database';
import { createApi } from '../core';
import { ServerOptions, ConfigOptions } from '../types';
import { HealthOptions, HttpDependencyConfig } from '../types';
import fs from 'fs';
import yaml from 'yaml';
import path from 'path';

// Import startup functions
import { setDefaultConfig } from "./config/defaults";
import { initializeDatabase, closeDatabase } from './database';
import { setupAuth } from '../middleware';
import { setupHealthChecks } from './health';
import { setupDocumentation } from './documentation';

// Import hook execution functions
import {
  executeBeforeInit,
  executeAfterConfigLoaded,
  executeAfterApiCreated,
  executeAfterDatabaseInit,
  executeAfterRoutesSetup,
  executeBeforeServerStart,
  executeAfterServerStart,
  executeBeforeShutdown,
  executeAfterDatabaseClosed,
  executeAfterServerClosed
} from './hooks';
import { applyErrorHandler } from "../middleware";

/**
 * Standard secret keys to mask in logs
 */
const DEFAULT_SECRET_KEYS = [
  'database.password',
  'database.url',
  'auth.jwt.secret',
  'auth.azure.clientSecret',
  'apiKeys'
];

/**
 * Initialize and load application configuration
 *
 * @param logger Logger instance
 * @param options Configuration options
 * @returns Initialized ConfigManager
 */
export async function initializeConfig(
  logger: Logger,
  options: ConfigOptions = {}
): Promise<ConfigManager> {
  try {
    // Get environment, but don't modify NODE_ENV directly
    const runtimeEnv = process.env.NODE_ENV || 'development';
    const environment = options.environment || runtimeEnv;
    const projectRoot = process.cwd();

    logger.info(`Current working directory: ${projectRoot}`);
    logger.info(`Environment: ${environment}`);

    // Determine config file path with fallbacks
    const configFilePaths = [
      // Explicit path if provided
      options.path,
      // Environment variable if set
      process.env.CONFIG_PATH,
      // Environment-specific config file
      path.join(projectRoot, 'config', `config.${environment}.yaml`),
      path.join(projectRoot, 'config', `config.${environment}.yml`),
      // Default config file
      path.join(projectRoot, 'config', 'config.yaml'),
      path.join(projectRoot, 'config', 'config.yml'),
      // Root directory config
      path.join(projectRoot, `config.${environment}.yaml`),
      path.join(projectRoot, `config.${environment}.yml`),
      path.join(projectRoot, 'config.yaml'),
      path.join(projectRoot, 'config.yml')
    ].filter(Boolean) as string[];

    // Debug log all possible paths
    logger.debug('Searching for config file in the following locations:');
    configFilePaths.forEach(path => logger.debug(`- ${path}`));

    // Find first existing config file
    let configFilePath = null;
    for (const filePath of configFilePaths) {
      logger.debug(`Checking if config file exists at: ${filePath}`);
      if (fs.existsSync(filePath)) {
        configFilePath = filePath;
        logger.debug(`Found config file at: ${filePath}`);
        break;
      }
    }

    logger.info('Initializing configuration', {
      configFilePath: configFilePath || 'using defaults',
      environment
    });

    // Create config manager with the basic options
    const secretKeys = [...DEFAULT_SECRET_KEYS, ...(options.secretKeys || [])];

    const config = createConfigManager({
      environment,
      logger,
      secretKeys,
    });

    // Set environment
    config.set('environment', environment);

    // Store the actual NODE_ENV for reference
    config.set('nodeEnv', runtimeEnv);

    // Set service name if provided
    if (options.serviceName) {
      config.set('service.name', options.serviceName);
    }

    // Try to load and parse the configuration file
    let loadedFromFile = false;

    if (configFilePath) {
      try {
        logger.debug(`Reading config file: ${configFilePath}`);
        const rawConfig = fs.readFileSync(configFilePath, 'utf8');
        logger.debug(`Config file content length: ${rawConfig.length} bytes`);

        const configFileContent = yaml.parse(rawConfig);
        logger.debug('Successfully parsed YAML content');

        // Add parsed config to the config manager
        if (configFileContent && typeof configFileContent === 'object') {
          // Debug log the top-level keys in the config
          logger.debug(`Config contains the following top-level keys: ${Object.keys(configFileContent).join(', ')}`);

          Object.entries(configFileContent).forEach(([key, value]) => {
            config.set(key, value);
            logger.debug(`Added config key: ${key}`);
          });

          logger.info('Configuration loaded from file successfully', {
            path: configFilePath
          });

          loadedFromFile = true;
        } else {
          logger.error('Invalid configuration format in file', {
            path: configFilePath,
            content: typeof configFileContent
          });
        }
      } catch (parseError) {
        logger.error('Error loading or parsing config file:', parseError as Error);
      }
    } else {
      logger.warn('No configuration file found, using defaults');

      // List files in the config directory
      try {
        const configDir = path.join(projectRoot, 'config');
        if (fs.existsSync(configDir)) {
          const files = fs.readdirSync(configDir);
          logger.debug(`Files in config directory: ${files.join(', ')}`);
        }
      } catch (err) {
        logger.debug('Could not list files in config directory', err);
      }
    }

    // Apply default configuration values if needed
    if (!loadedFromFile) {
      logger.debug('Applying default configuration because no file was loaded');
      setDefaultConfig(config, logger, environment, options.defaults);
    } else if (options.defaults) {
      logger.debug('Applying defaults only for missing values');

      // Create a temporary config to hold default values
      const tempConfig = createConfigManager({
        environment,
        logger,
        secretKeys: [],
      });

      // Set defaults in the temporary config
      setDefaultConfig(tempConfig, logger, environment, options.defaults);

      // Only apply defaults for keys that don't exist in main config
      const allDefaults = tempConfig.getAll();

      // Function to recursively merge default values only where they don't already exist
      const mergeDefaults = (target: any, source: any, prefix: string = '') => {
        for (const key in source) {
          const fullPath = prefix ? `${prefix}.${key}` : key;

          // Skip if the value already exists in the main config
          if (config.get(fullPath) !== undefined) {
            continue;
          }

          // For objects, recurse
          if (source[key] !== null && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            mergeDefaults(target, source[key], fullPath);
          } else {
            // Set the default value
            config.set(fullPath, source[key]);
            logger.debug(`Applied default value for: ${fullPath}`);
          }
        }
      };

      mergeDefaults(config, allDefaults);
    }

    // Process environment variables for configuration
    logger.debug('Processing environment variables in configuration');
    processEnvironmentVariables(config);

    // Log masked configuration values
    const configInfo: Record<string, any> = {
      environment: config.getEnvironment(),
      nodeEnv: config.get('nodeEnv'),
      server: {
        port: config.get<number>('server.port', 3000),
        host: config.get<string>('server.host', '0.0.0.0')
      }
    };

    // Add database info if configured
    const dbHost = config.get<string>('database.host', '');
    const dbUser = config.get<string>('database.user', '');
    const dbName = config.get<string>('database.database', '');

    if (dbHost) {
      configInfo['database'] = {
        host: dbHost,
        user: dbUser,
        database: dbName,
        password: '****'
      };
    }

    logger.info('Configuration initialized', configInfo);

    return config;
  } catch (error) {
    logger.error('Failed to initialize configuration', error instanceof Error ? error : new Error('Unknown error'));
    throw error;
  }
}

/**
 * Process environment variables for configuration
 * Replaces ${ENV_VAR} patterns in config values with actual environment variable values
 */
function processEnvironmentVariables(config: ConfigManager): void {
  const processValue = (value: any): any => {
    if (typeof value === 'string') {
      // Replace ${ENV_VAR} with environment variable value
      return value.replace(/\${([^}]+)}/g, (match, envVar) => {
        // Check if it contains a default value using the :-syntax
        if (envVar.includes(':-')) {
          const [envName, defaultValue] = envVar.split(':-');
          return process.env[envName] || defaultValue || '';
        }
        return process.env[envVar] || match;
      });
    } else if (Array.isArray(value)) {
      return value.map(processValue);
    } else if (value !== null && typeof value === 'object') {
      const processed: Record<string, any> = {};
      for (const [key, val] of Object.entries(value)) {
        processed[key] = processValue(val);
      }
      return processed;
    }
    return value;
  };

  // Get all config entries and process them
  const allConfig = config.getAll();

  // Process each key-value pair
  Object.entries(allConfig).forEach(([key, value]) => {
    const processedValue = processValue(value);

    // Only set if the value changed
    if (value !== processedValue) {
      config.set(key, processedValue);
    }
  });
}

/**
 * Start an API server with provided configuration
 *
 * @param options Server configuration options
 * @returns A promise that resolves to the server instance
 */
export async function startServer(options: ServerOptions): Promise<Server> {
  let logger: Logger | undefined;
  let app: Application | null = null;
  let config: ConfigManager | null = null;
  let dbConnection: DatabaseConnection | null = null;
  let server: Server | null = null;

  try {
    // Initialize logger first
    logger = createLogger({
      serviceName: options.serviceName,
      level: options.logLevel || process.env.LOG_LEVEL || 'info',
      prettyPrint: process.env.NODE_ENV !== 'production'
    });

    // Execute beforeInit hook
    await executeBeforeInit(options.hooks, logger);

    // Initialize config next
    config = await initializeConfig(logger, {
      path: options.config?.path,
      defaults: options.config?.defaults,
      secretKeys: options.config?.secretKeys,
      environment: options.config?.environment,
      serviceName: options.serviceName
    });

    // Early exit if config initialization failed
    if (!config) {
      throw new Error('Failed to initialize configuration');
    }

    // Execute afterConfigLoaded hook
    await executeAfterConfigLoaded(options.hooks, config, logger);

    // Initialize the API framework with options
    const apiResult = await createApi(config, logger, {
      // Core express settings
      cors: options.api?.cors ?? config.get('api.security.cors', true),
      corsOptions: options.api?.corsOptions ?? config.get('api.security.corsOptions'),
      bodyLimit: options.api?.bodyLimit ?? config.get('api.bodyLimit', '1mb'),
      compression: options.api?.compression ?? config.get('api.compression', true),
      helmet: options.api?.helmet ?? config.get('api.security.helmet', process.env.NODE_ENV === 'production'),

      // Request tracking
      requestLogging: options.api?.requestLogging ?? config.get('api.requestLogging', true),
      correlationId: options.api?.correlationId ?? config.get('api.correlationId.enabled', true),
      correlationIdOptions: options.api?.correlationIdOptions ?? config.get('api.correlationId'),

      // Route settings
      basePath: options.api?.basePath ?? config.get('api.basePath', '/api'),

      // Performance options
      metrics: options.api?.metrics ?? config.get('api.metrics'),
      rateLimit: options.api?.rateLimit ?? config.get('api.rateLimit'),

      // Response formatting
      responseFormat: options.api?.responseFormat ?? config.get('api.responseFormat'),

      // Custom middleware
      preMiddlewares: options.api?.preMiddlewares,
      postMiddlewares: options.api?.postMiddlewares,
    });

    app = apiResult.app;

    logger.info('API framework initialized successfully');

    // Store logger and config in app locals
    if (app) {
      app.locals.logger = logger;
      app.locals.config = config;
    }

    // Execute afterApiCreated hook
    if (app) {
      await executeAfterApiCreated(options.hooks, app, config, logger);
    }

    // Initialize database connection if configured
    const dbRequired = options.database?.required ?? config.get('database.required', true);

    try {
      dbConnection = await initializeDatabase(config, logger, {
        type: options.database?.type ?? config.get('database.type', 'postgres'),
        connection: options.database?.connection,
        init: options.database?.init,
        required: dbRequired,
      });

      logger.info('Database connection established successfully');

      // Execute afterDatabaseInit hook
      if (dbConnection) {
        await executeAfterDatabaseInit(options.hooks, dbConnection, config, logger);
      }
    } catch (dbError) {
      if (dbRequired) {
        logger.error('Failed to initialize required database connection', dbError as Error);
        throw dbError; // Re-throw if database is required
      } else {
        logger.warn('Failed to initialize database, but continuing as it is marked optional', dbError as Error);
      }
    }

    // Setup authentication
    const authType = options.auth?.type ?? config.get('auth.type', process.env.NODE_ENV === 'production' ? 'jwt' : 'none');
    const authRequired = options.auth?.required ?? config.get('auth.required', process.env.NODE_ENV === 'production');
    let authMiddleware;


    try {
      if (!app) {
        throw new Error('Application was not initialized properly');
      }

      authMiddleware = await setupAuth(app, config, logger, {
        type: authType,
        azure: options.auth?.azure,
        jwt: options.auth?.jwt,
        customAuthFn: options.auth?.customAuthFn,
      });

      logger.info(`Authentication setup successfully with type: ${authType}`);
    } catch (authError) {
      if (authRequired) {
        logger.error('Failed to initialize required authentication', authError as Error);
        throw authError; // Re-throw if auth is required
      } else {
        logger.warn('Failed to initialize authentication, but continuing as it is marked optional', authError as Error);
        // Define a pass-through middleware when auth is disabled/failed
        authMiddleware = (req: any, res: any, next: any) => next();
      }
    }

    // Setup health checks if enabled
    const healthEnabled = options.health?.enabled ?? config.get('health.enabled', true);

    if (healthEnabled && app) {
      // Prepare health check options
      const healthOptions: HealthOptions = {
        endpoint: options.health?.endpoint ?? config.get('health.endpoint', '/health'),
        memoryThreshold: options.health?.memoryThreshold ?? config.get('health.memory.threshold', 85),
        cacheTime: options.health?.cacheTime ?? config.get('health.cacheTime', 10000),
        customIndicators: options.health?.customIndicators,
      };

      // Load HTTP dependencies from config if not provided in options
      if (!options.health?.httpDependencies) {
        const configDeps = config.get<Array<Record<string, any>>>('health.dependencies.http', []);

        if (configDeps.length > 0) {
          healthOptions.httpDependencies = configDeps as HttpDependencyConfig[];
        }
      } else {
        healthOptions.httpDependencies = options.health.httpDependencies;
      }

      await setupHealthChecks(app, dbConnection, config, logger, healthOptions);
      logger.info('Health checks configured successfully');
    }

    // Setup routes
    if (!app) {
      throw new Error('Application was not initialized properly');
    }

    await options.setupRoutes(apiResult, dbConnection, authMiddleware, logger);

    logger.info('Routes configured successfully');

    // Execute afterRoutesSetup hook
    await executeAfterRoutesSetup(options.hooks, app, config, logger);

    // Setup API documentation if enabled
    const docsEnabled = options.docs?.enabled ?? config.get('api.docs.enabled', process.env.NODE_ENV !== 'production');

    if (docsEnabled && app) {
      await setupDocumentation(app, config, logger, {
        type: options.docs?.type ?? config.get('api.docs.type', 'swagger'),
        endpoint: options.docs?.endpoint ?? config.get('api.docs.endpoint', '/api-docs'),
        title: options.docs?.title ?? config.get('api.docs.title', options.serviceName),
        version: options.docs?.version ?? config.get('api.docs.version', '1.0.0'),
        description: options.docs?.description ?? config.get('api.docs.description', `${options.serviceName} API Documentation`),
        customCss: options.docs?.customCss,
        apiPaths: options.docs?.apiPaths,
        oauth2: options.docs?.oauth2,
      });

      logger.info('API documentation configured successfully');
    }

    // Apply error handler - this adds the error handling middleware after all routes
    if (app) {
      applyErrorHandler(app, logger);
      logger.info('Error handling configured');
    }

    // Execute beforeServerStart hook
    if (app) {
      await executeBeforeServerStart(options.hooks, app, config, logger);
    }

    // Start the server
    const PORT = config.get<number>('server.port', 3000);
    const HOST = config.get<string>('server.host', '0.0.0.0');
    const environment = config.getEnvironment();

    return new Promise((resolve) => {
      if (!app) {
        throw new Error('Application was not initialized properly');
      }

      server = app.listen(PORT, HOST, async () => {
        const address = server?.address();
        const actualPort = typeof address === 'object' && address ? address.port : PORT;

        logger!.info('Server started', {
          port: actualPort,
          host: HOST,
          environment
        });

        // Log API endpoints
        const basePath = config ? config.get<string>('api.basePath', '/api') : '/api';
        logger!.info(`API endpoints available at: http://${HOST === '0.0.0.0' ? 'localhost' : HOST}:${actualPort}${basePath}`);

        // If API docs are enabled
        if (docsEnabled) {
          const docsEndpoint = config ? config.get<string>('api.docs.endpoint', '/api-docs') : '/api-docs';
          logger!.info(`API documentation available at: http://${HOST === '0.0.0.0' ? 'localhost' : HOST}:${actualPort}${docsEndpoint}`);
        }

        // Execute afterServerStart hook
        if (app) {
          await executeAfterServerStart(options.hooks, actualPort, HOST, environment, app, logger!);
        }

        if (server) {
          resolve(server);
        } else {
          logger!.error('Server instance is null after starting');
          throw new Error('Server instance is null after starting');
        }
      });

      // Register graceful shutdown
      if (server && config) {
        setupGracefulShutdown(server, dbConnection, config, logger!, options);
      }
    });
  } catch (error) {
    if (logger) {
      logger.error('Failed to start application', error as Error);
    } else {
      console.error('Failed to start application', error);
    }
    throw error;
  }
}

/**
 * Set up graceful shutdown handling
 *
 * @param server HTTP server instance
 * @param dbConnection Database connection
 * @param config Configuration manager
 * @param logger Logger instance
 * @param options Server options
 */
function setupGracefulShutdown(
  server: Server,
  dbConnection: DatabaseConnection | null,
  config: ConfigManager,
  logger: Logger,
  options: ServerOptions
): void {
  // Ensure config is not null (this should never happen here since we've already validated it earlier)
  const shutdownTimeout = config.get<number>('server.shutdownTimeout', 10000);

  // Handle SIGTERM signal (e.g., from Docker, Kubernetes)
  process.on('SIGTERM', async () => {
    logger.info('SIGTERM signal received, shutting down gracefully');

    // Execute beforeShutdown hook
    await executeBeforeShutdown(options.hooks, logger);

    // Close database connection first
    if (dbConnection) {
      try {
        await closeDatabase(logger);
        logger.info('Database connection closed successfully');

        // Execute afterDatabaseClosed hook
        await executeAfterDatabaseClosed(options.hooks, logger);
      } catch (error) {
        logger.error('Error closing database connection', error as Error);
      }
    }

    // Then close the HTTP server
    server.close(async () => {
      logger.info('HTTP server closed');

      // Execute afterServerClosed hook
      await executeAfterServerClosed(options.hooks, logger);

      process.exit(0);
    });

    // Force close after timeout
    setTimeout(() => {
      logger.error(`Could not close connections in time (${shutdownTimeout}ms), forcefully shutting down`);
      process.exit(1);
    }, shutdownTimeout);
  });

  // Handle SIGINT signal (e.g., Ctrl+C)
  process.on('SIGINT', () => {
    logger.info('SIGINT signal received, shutting down gracefully');
    process.emit('SIGTERM'); // Reuse SIGTERM handler
  });

  // Handle unhandled rejections
  process.on('unhandledRejection', (reason: any) => {
    logger.error('Unhandled rejection', reason instanceof Error ? reason : new Error(String(reason)));
    // In production, you might want to exit the process
    if (process.env.NODE_ENV === 'production') {
      process.exit(1);
    }
  });
}
